import React, { useState, useEffect, useCallback } from 'react';

/**
 * Shows a My Hive post's or comment's pictures and videos (Jenn, 2026-10-24):
 * one fills the width, several sit in a tidy grid (the 5th+ tile says "+N").
 * Tapping any opens it large, with arrows to step through the rest.
 * Each picture/video carries the member's own description as its alt text.
 */
const altFor = (m) => m.alt_text || (m.kind === 'video' ? 'Video shared in the Hive' : 'Picture shared in the Hive');

function Tile({ m, onOpen, className, overlay }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      className={`relative overflow-hidden rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-reset-terracotta ${className}`}
      aria-label={m.kind === 'video' ? `Play video: ${altFor(m)}` : `Open picture: ${altFor(m)}`}
    >
      {m.kind === 'video' ? (
        <>
          <video src={`${m.url}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-cover" aria-hidden="true" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-11 h-11 rounded-full bg-black/55 text-white flex items-center justify-center text-lg pl-0.5">▶</span>
          </span>
        </>
      ) : (
        <img src={m.url} alt={altFor(m)} loading="lazy" className="w-full h-full object-cover" />
      )}
      {overlay && (
        <span className="absolute inset-0 bg-black/50 text-white text-xl font-semibold flex items-center justify-center">{overlay}</span>
      )}
    </button>
  );
}

export default function MediaGrid({ media, size = 'post' }) {
  const [open, setOpen] = useState(null);
  const list = media || [];
  const close = useCallback(() => setOpen(null), []);
  const step = useCallback((d) => setOpen(i => (i === null ? null : (i + d + list.length) % list.length)), [list.length]);

  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close, step]);

  if (!list.length) return null;

  const small = size === 'comment';
  const shown = list.slice(0, 4);
  const extra = list.length - shown.length;

  let grid;
  if (list.length === 1) {
    grid = <Tile m={list[0]} onOpen={() => setOpen(0)} className={`w-full ${small ? 'max-w-xs h-48' : 'max-h-96 h-72'}`} />;
  } else {
    grid = (
      <div className={`grid grid-cols-2 gap-1.5 ${small ? 'max-w-xs' : ''}`}>
        {shown.map((m, i) => (
          <Tile
            key={m.id}
            m={m}
            onOpen={() => setOpen(i)}
            className={small ? 'h-24' : 'h-36 sm:h-44'}
            overlay={i === 3 && extra > 0 ? `+${extra}` : null}
          />
        ))}
      </div>
    );
  }

  const current = open !== null ? list[open] : null;

  return (
    <div className="mt-3" onClick={(e) => e.stopPropagation()}>
      {grid}
      {current && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Pictures and videos"
        >
          <div className="relative max-w-4xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            {current.kind === 'video' ? (
              <video key={current.id} src={current.url} controls autoPlay playsInline className="max-h-[80vh] max-w-full rounded-lg bg-black" aria-label={altFor(current)} />
            ) : (
              <img key={current.id} src={current.url} alt={altFor(current)} className="max-h-[80vh] max-w-full rounded-lg object-contain" />
            )}
            {current.alt_text && <p className="text-white/85 text-sm mt-3 text-center max-w-2xl">{current.alt_text}</p>}
            {list.length > 1 && <p className="text-white/60 text-xs mt-1">{open + 1} of {list.length}</p>}
          </div>
          <button type="button" onClick={close} aria-label="Close" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 text-white text-xl hover:bg-white/25">×</button>
          {list.length > 1 && (
            <>
              <button type="button" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous" className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 text-white text-2xl hover:bg-white/25">‹</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next" className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 text-white text-2xl hover:bg-white/25">›</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
